import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import SchoolLogo from "@/components/SchoolLogo";
import ImageUploadField from "@/components/ImageUploadField";
import { GraduationCap, Plus, Search, X, Archive, RotateCcw, GitMerge, Pencil, Users } from "lucide-react";

const ASSOCIATIONS = ["NCAA", "NAIA", "NJCAA", "CCCAA", "U SPORTS", "CCAA", "Other"];
const DIVISIONS = ["NCAA D1", "NCAA D2", "NCAA D3", "NAIA", "JUCO", "U Sports", "CCAA", "Pro", "Other"];
const EMPTY = { name: "", city: "", region: "", country: "USA", website: "", association: "", division: "", logo_url: "", logo_dark_url: "" };

export default function AdminSchools() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ active: 0, archived: 0 });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // school object or EMPTY for new
  const [merge, setMerge] = useState(null); // {source}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/schools`, { params: { q: q || undefined, status: statusFilter } });
      setItems(data.items || []);
      setCounts(data.counts || { active: 0, archived: 0 });
    } catch { toast.error("Could not load schools"); } finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const archive = async (s, archived) => {
    try {
      await api.post(`/admin/schools/${s.id}/archive`, null, { params: { archived } });
      toast.success(archived ? "School archived" : "School restored");
      load();
    } catch { toast.error("Action failed"); }
  };

  const runBackfill = async () => {
    try {
      const { data } = await api.post(`/admin/schools/backfill-from-commitments`);
      toast.success(`Backfill: ${data.linked} linked, ${data.created_schools} created`);
      load();
    } catch { toast.error("Backfill failed"); }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 label-overline text-[#E63946]"><GraduationCap className="w-4 h-4" /> Directory</div>
            <h1 className="font-display text-3xl uppercase tracking-tight">Schools &amp; Colleges</h1>
            <p className="text-sm text-slate-500 mt-1">Reusable school records. Upload a logo once — it appears on every committed player.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="school-backfill-btn" onClick={runBackfill} className="border border-slate-300 text-slate-600 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:border-[#0A192F]">Link Existing Commitments</button>
            <button data-testid="school-add-btn" onClick={() => setEditing({ ...EMPTY })} className="bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add School</button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center border border-slate-300 rounded-sm bg-white flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 ml-2" />
            <input data-testid="school-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search schools…" className="flex-1 px-2 py-2 text-sm outline-none" />
          </div>
          <div className="flex gap-1">
            {[["active", `Active (${counts.active})`], ["archived", `Archived (${counts.archived})`], ["all", "All"]].map(([v, l]) => (
              <button key={v} data-testid={`school-filter-${v}`} onClick={() => setStatusFilter(v)}
                className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border ${statusFilter === v ? "bg-[#0A192F] text-white border-[#0A192F]" : "border-slate-300 text-slate-600"}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="mt-4 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">School</th>
                <th className="px-4 py-3">Division</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3 text-center">Committed</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No schools found.</td></tr>
              ) : items.map((s) => (
                <tr key={s.id} data-testid={`school-row-${s.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <SchoolLogo name={s.name} logoUrl={s.logo_url} size="md" />
                      <div>
                        <div className="font-semibold text-[#0A192F]">{s.name}{s.status === "archived" && <span className="ml-2 text-[9px] uppercase bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-sm">Archived</span>}</div>
                        {s.association && <div className="text-[11px] text-slate-400">{s.association}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{s.division || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{[s.city, s.region, s.country].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5 text-slate-400" />{s.committed_count}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`school-edit-${s.id}`} onClick={() => setEditing(s)} title="Edit" className="p-1.5 text-slate-500 hover:text-[#0A192F]"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`school-merge-${s.id}`} onClick={() => setMerge({ source: s })} title="Merge into another" className="p-1.5 text-slate-500 hover:text-[#0A192F]"><GitMerge className="w-4 h-4" /></button>
                      {s.status === "archived"
                        ? <button data-testid={`school-restore-${s.id}`} onClick={() => archive(s, false)} title="Restore" className="p-1.5 text-slate-500 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>
                        : <button data-testid={`school-archive-${s.id}`} onClick={() => archive(s, true)} title="Archive" className="p-1.5 text-slate-500 hover:text-amber-600"><Archive className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <SchoolModal school={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {merge && <MergeModal source={merge.source} allSchools={items} onClose={() => setMerge(null)} onDone={() => { setMerge(null); load(); }} />}
    </AdminLayout>
  );
}

function SchoolModal({ school, onClose, onSaved }) {
  const isNew = !school.id;
  const [form, setForm] = useState({
    name: school.name || "", city: school.city || "", region: school.region || "", country: school.country || "USA",
    website: school.website || "", association: school.association || "", division: school.division || "",
    logo_url: school.logo_url || "", logo_dark_url: school.logo_dark_url || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("School name is required"); return; }
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      if (isNew) {
        await api.post(`/admin/schools`, payload);
        toast.success("School created");
      } else {
        const { data } = await api.patch(`/admin/schools/${school.id}`, payload);
        toast.success(data.commitments_resynced ? `Saved · ${data.commitments_resynced} player profiles updated` : "School updated");
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) toast.error(typeof d === "object" ? d.message : "A similar school already exists");
      else toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="school-modal" className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="font-display uppercase tracking-tight">{isNew ? "Add School" : "Edit School"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label-overline text-[10px]">Name *</label>
            <input data-testid="school-modal-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-overline text-[10px]">City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
            <div><label className="label-overline text-[10px]">Province / State</label><input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-overline text-[10px]">Country</label><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
            <div><label className="label-overline text-[10px]">Website</label><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="school.edu" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-overline text-[10px]">Association</label>
              <select value={form.association} onChange={(e) => setForm({ ...form, association: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"><option value="">—</option>{ASSOCIATIONS.map((a) => <option key={a} value={a}>{a}</option>)}</select>
            </div>
            <div><label className="label-overline text-[10px]">Division</label>
              <select value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"><option value="">—</option>{DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}</select>
            </div>
          </div>
          <ImageUploadField label="Primary Logo" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testid="school-modal-logo" aspect="aspect-square" />
          <ImageUploadField label="Dark-Background Logo (optional)" value={form.logo_dark_url} onChange={(v) => setForm({ ...form, logo_dark_url: v })} testid="school-modal-logo-dark" aspect="aspect-square" />
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-slate-300 rounded-sm">Cancel</button>
          <button data-testid="school-modal-save" onClick={save} disabled={busy} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-[#0A192F] text-white rounded-sm disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function MergeModal({ source, allSchools, onClose, onDone }) {
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const options = allSchools.filter((s) => s.id !== source.id && s.status !== "archived");

  const doMerge = async () => {
    if (!targetId) { toast.error("Pick a target school"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/schools/merge`, { source_id: source.id, target_id: targetId });
      toast.success(`Merged · ${data.commitments_moved} commitments moved`);
      onDone();
    } catch { toast.error("Merge failed"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="school-merge-modal" className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="font-display uppercase tracking-tight">Merge Duplicate</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600">Move all commitments from <strong>{source.name}</strong> into another school, then archive this one.</p>
          <div>
            <label className="label-overline text-[10px]">Keep this school (target)</label>
            <select data-testid="merge-target-select" value={targetId} onChange={(e) => setTargetId(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">Select target school…</option>
              {options.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-slate-300 rounded-sm">Cancel</button>
          <button data-testid="merge-confirm" onClick={doMerge} disabled={busy} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-[#E63946] text-white rounded-sm disabled:opacity-60">{busy ? "Merging…" : "Merge & Archive"}</button>
        </div>
      </div>
    </div>
  );
}
